import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useToast } from '@aslanpay/portal-common-lib';

import { DEDUCTION_ADJUSTMENTS_QUERY_KEY } from 'app/hooks/useFetchDeductionAdjustments';

import DeductionAdjustmentsApiHandler from 'app/services/deductionAdjustmentsApiHandler';

import {
  CreateDeductionAdjustmentRequest,
  DeductionAdjustment,
} from 'app/types/deductionAdjustments';

const DEFAULT_ERROR_DESCRIPTION = 'Sorry, the adjustment could not be added. Please try again.';

const getErrorDescription = (error: Error) =>
  (axios.isAxiosError<{ message?: string }>(error) && error.response?.data?.message) ||
  DEFAULT_ERROR_DESCRIPTION;

/** Adds a deduction adjustment to a person's open deduction report. */
const useCreateDeductionAdjustment = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<DeductionAdjustment, Error, CreateDeductionAdjustmentRequest>(
    (request) => DeductionAdjustmentsApiHandler.createAdjustment(request),
    {
      onSuccess: () => {
        toast({ position: 'top-right', status: 'success', title: 'Deduction adjustment added' });
      },
      onError: (error) => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: getErrorDescription(error),
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: [DEDUCTION_ADJUSTMENTS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: ['fetchIndividualDeductionsReportsPage'] });
        queryClient.invalidateQueries({ queryKey: ['fetchIndividualDeductionsReport'] });
      },
    }
  );
};

export default useCreateDeductionAdjustment;
